// Connectivity monitor for offline mode.
//
// "Online" here means the XANO BACKEND is reachable — not merely that the
// browser has a network interface, and not that the app's static host
// responds. The probe targets a public single-PK Xano endpoint
// (GET /public/organizations/{id}) so that a backend outage (DB down, 5xx,
// hang) counts as offline while a static-host outage does not.
//
// Hysteresis: two consecutive failures flip to offline; a single success
// flips back online — unless a reconnect gate is set (see setReconnectGate):
// a client holding queued offline writes stays offline, still probing, until
// the gate has replayed them, so nothing it does meanwhile can be overwritten
// by an older queued write landing later. The browser's `offline` event flips immediately
// (debounced 500ms); its `online` event only triggers a probe — the flip
// happens on probe success, never on the event alone.
//
// The probe timer no-ops for orgs without offline mode (nothing consumes
// isOnline() there); this intentionally retires the old dead /api-heartbeat
// ping for those orgs.

import { isOfflineModeEnabled, getOfflineOrganizationId, disableOfflineModeRemotely, settingsEnableOfflineMode } from './featureFlag';
import { XANO_BASE_URL } from '../api/client';

// Online cadence 5 min (was 30 s, 2026-09-22): an active tab detects an
// outage through its own failing requests (reportBackendFailure probes at
// once), so the timer only covers idle tabs and the remote kill switch. At
// 30 s the probe was 11% of all platform requests at one clinic.
const PROBE_ONLINE_INTERVAL = 5 * 60 * 1000;
const PROBE_OFFLINE_INTERVAL = 10000;
const PROBE_TIMEOUT = 5000;
const DEBOUNCE_MS = 500;
const FAIL_THRESHOLD = 2;
// 2026-09-22: every tab that reconnected together probed in the same second
// (12/s on public/organizations after the outage); the interval now starts at
// a random phase, event-driven probes are spread, and a failing request can
// trigger at most one extra probe per FAILURE_PROBE_MIN_GAP_MS per tab.
const EVENT_PROBE_JITTER_MAX_MS = 3000;
const FAILURE_PROBE_MIN_GAP_MS = 10000;

class ConnectivityMonitor {
  constructor() {
    this._online = navigator.onLine;
    this._subscribers = new Set();
    this._probeTimer = null;
    this._probePhaseTimer = null;
    this._jitterTimer = null;
    this._lastFailureProbeAt = 0;
    this._debounceTimer = null;
    this._initialized = false;
    this._failStreak = 0;
    this._probing = null;
    this._reconnectGate = null;
    this._recovering = false;
  }

  initialize() {
    if (this._initialized) return;
    this._initialized = true;

    window.addEventListener('online', this._handleBrowserOnline);
    window.addEventListener('offline', this._handleBrowserOffline);
    document.addEventListener('visibilitychange', this._handleVisibility);

    this._startProbeTimer({ randomPhase: true });
  }

  isOnline() {
    return this._online;
  }

  // True while the backend is reachable again but this client is still held
  // offline by the reconnect gate (queued writes not yet replayed).
  isRecovering() {
    return this._recovering;
  }

  // gate(isHealthy) resolves true when the client may go online. isHealthy()
  // turns false as soon as a probe fails during the hold.
  setReconnectGate(gate) {
    this._reconnectGate = gate;
  }

  subscribe(callback) {
    this._subscribers.add(callback);
    return () => this._subscribers.delete(callback);
  }

  unsubscribe(callback) {
    this._subscribers.delete(callback);
  }

  // Called by the API middleware when a request fails in a way that points at
  // the backend (network error, timeout, 5xx) so an outage is confirmed
  // within one probe round-trip instead of waiting for the next tick.
  reportBackendFailure() {
    if (!isOfflineModeEnabled()) return;
    this._failStreak += 1;
    if (this._failStreak >= FAIL_THRESHOLD && this._online) {
      this._setOnline(false);
    } else {
      const now = Date.now();
      if (now - this._lastFailureProbeAt < FAILURE_PROBE_MIN_GAP_MS) return;
      this._lastFailureProbeAt = now;
      this.probeNow();
    }
  }

  // One probe after a short random delay; a second call inside the window
  // just resets the timer.
  _probeJittered() {
    clearTimeout(this._jitterTimer);
    this._jitterTimer = setTimeout(() => {
      this._jitterTimer = null;
      this.probeNow();
    }, Math.random() * EVENT_PROBE_JITTER_MAX_MS);
  }

  async probeNow() {
    if (!isOfflineModeEnabled()) return;
    if (this._probing) return this._probing;
    this._probing = this._probe().finally(() => {
      this._probing = null;
    });
    return this._probing;
  }

  _handleBrowserOnline = () => {
    // The browser regaining an interface says nothing about Xano — probe.
    clearTimeout(this._debounceTimer);
    this._probeJittered();
  };

  _handleBrowserOffline = () => {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this._failStreak = FAIL_THRESHOLD;
      this._setOnline(false);
    }, DEBOUNCE_MS);
  };

  _handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      this._probeJittered();
    }
  };

  // Not awaited by the probe: probing continues at the offline cadence while
  // the gate holds, which is what keeps isHealthy() current.
  _goOnline() {
    if (this._online || this._recovering) return;
    if (!this._reconnectGate) {
      this._setOnline(true);
      return;
    }
    this._recovering = true;
    this._notify();
    Promise.resolve()
      .then(() => this._reconnectGate(() => this._failStreak === 0))
      .catch(() => false)
      .then((ok) => {
        this._recovering = false;
        if (ok && this._failStreak === 0) this._setOnline(true);
        else this._notify();
      });
  }

  _setOnline(online) {
    if (this._online === online) return;
    this._online = online;
    this._notify();
    this._startProbeTimer();
  }

  _notify() {
    for (const callback of this._subscribers) {
      try {
        callback(this._online);
      } catch (e) {
        console.error('[ConnectivityMonitor] Subscriber error:', e);
      }
    }
  }

  // randomPhase: only at initialize, so tabs started together do not probe
  // together for the rest of their life. A state flip keeps the old cadence
  // (the first offline probe must still land 10 s after the flip).
  _startProbeTimer({ randomPhase = false } = {}) {
    this._stopProbeTimer();
    const interval = this._online ? PROBE_ONLINE_INTERVAL : PROBE_OFFLINE_INTERVAL;
    const start = () => {
      this._probePhaseTimer = null;
      this._probeTimer = setInterval(() => {
        if (!isOfflineModeEnabled()) return; // no probe traffic for non-offline orgs
        this.probeNow();
      }, interval);
    };
    if (randomPhase) this._probePhaseTimer = setTimeout(start, Math.random() * interval);
    else start();
  }

  _stopProbeTimer() {
    if (this._probeTimer) {
      clearInterval(this._probeTimer);
      this._probeTimer = null;
    }
    if (this._probePhaseTimer) {
      clearTimeout(this._probePhaseTimer);
      this._probePhaseTimer = null;
    }
  }

  async _probe() {
    const orgId = getOfflineOrganizationId();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT);

    try {
      const response = await fetch(
        `${XANO_BASE_URL}/public/organizations/${orgId ?? 0}`,
        { method: 'GET', cache: 'no-store', signal: controller.signal }
      );

      // Any answer below 500 proves the backend is up (404 for an unknown id
      // is still reachability); 5xx means the backend is in trouble.
      if (response.status < 500) {
        this._failStreak = 0;
        if (response.ok) await this._checkRemoteDisable(response, orgId);
        this._goOnline();
      } else {
        this._recordFailure();
      }
    } catch {
      // Network error, DNS failure, or our timeout abort.
      this._recordFailure();
    } finally {
      clearTimeout(timeout);
    }
  }

  // Remote kill switch. Acts only on a positive signal: the row is this org,
  // it carries a settings object, and neither flag key there is true. A body
  // that cannot be parsed or has no settings changes nothing.
  async _checkRemoteDisable(response, orgId) {
    try {
      const org = await response.json();
      if (!org || org.id !== orgId) return;
      if (!org.settings || typeof org.settings !== 'object') return;
      if (!settingsEnableOfflineMode(org.settings)) disableOfflineModeRemotely();
    } catch {
      // ignore
    }
  }

  _recordFailure() {
    this._failStreak += 1;
    if (this._failStreak >= FAIL_THRESHOLD) {
      this._setOnline(false);
    }
  }

  destroy() {
    this._stopProbeTimer();
    clearTimeout(this._debounceTimer);
    clearTimeout(this._jitterTimer);
    this._jitterTimer = null;
    window.removeEventListener('online', this._handleBrowserOnline);
    window.removeEventListener('offline', this._handleBrowserOffline);
    document.removeEventListener('visibilitychange', this._handleVisibility);
    this._subscribers.clear();
    this._initialized = false;
  }
}

export const connectivityMonitor = new ConnectivityMonitor();
